import PrivacyPolicy from '@/components/pages/PrivacyPolicy.vue';

export const videoLinkRoute = {
  path: '/video/:videoId',
  name: 'VideoLink',
  component: () => import('@/components/pages/SharedVideo.vue'),
  meta: {
    skipBrowserNotSupported: true,
    title: 'Video | Real Estate Video Tours',
    publicPage: true
  }
};

export const videoEditRoutes = {
  path: '/',
  component: {
    template: '<router-view></router-view>'
  },
  children: [
    {
      path: '/videos/:videoId/edit',
      name: 'VideoEdit',
      meta: {
        roles: ['corp', 'manager', 'agent', 'contractor']
      },
      component: () => import('@/components/videos/edit/main.vue')
    }
  ]
};

export const reviewRedirectRoute = {
  path: '/video-review-process',
  component: () => import('@/components/pages/PageLoading.vue')
};

// Keep old LT/LS static routes redirects JIRA#DEV-8217
const redirectToLVTStatic = (to) => ({
  path: `/live-video-tour/static/${to.params.id}`,
  query: to.query,
  hash: to.hash
});

const legacyStaticRoutes = [
  '/live_session/static/:id',
  '/live_tour/static/:id'
].map((path) => ({
  path,
  redirect: redirectToLVTStatic
}));

// Keep old LT/LS routes redirects JIRA#DEV-8217
const legacyLiveRoutes = [
  '/live/sessions',
  '/live/sessions/schedule',
  '/live/tours',
  '/live/tours/schedule'
].map((path) => ({
  path,
  redirect: { name: 'LiveVideoTours' }
}));

export const inAppRoutes = {
  path: '/',
  component: () => import('@/components/layout/main.vue'),
  children: [
    ...legacyLiveRoutes,
    {
      path: '/home',
      name: 'Home',
      component: () => import('@/components/home/dashboard/main.vue'),
      meta: {
        roles: ['corp', 'manager', 'agent']
      }
    },
    {
      path: '/media',
      component: () => import('@/components/media/main.vue'),
      children: [
        {
          path: '',
          name: 'Media',
          meta: {
            roles: ['corp', 'manager', 'agent']
          },
          component: () => import('@/components/media/MediaView.vue')
        },
        {
          path: 'list',
          name: 'MediaList',
          meta: {
            roles: ['corp', 'manager', 'agent']
          },
          component: () => import('@/components/media/list/main.vue')
        }
      ]
    },
    { // Keep old `videos` routes redirects JIRA#DEV-7703
      path: '/videos',
      redirect: { name: 'Media' },
      children: [
        {
          path: 'list',
          redirect: { name: 'MediaList' }
        }
      ]
    },
    {
      path: '/home/analytics',
      component: () => import('@/components/home/analytics/main.vue'),
      name: 'MainAnalytics',
      redirect: { name: 'AnalyticsUsage' },
      children: [
        {
          path: 'usage',
          name: 'AnalyticsUsage',
          meta: {
            roles: ['corp', 'manager', 'agent']
          },
          component: () => import('@/components/home/analytics/usage/main.vue')
        },
        {
          path: 'engagement',
          name: 'AnalyticsEngagement',
          meta: {
            roles: ['corp', 'manager', 'agent']
          },
          component: () => import('@/components/home/analytics/engagement/main.vue')
        },
        {
          path: 'insights',
          name: 'AnalyticsInsights',
          meta: {
            roles: ['corp', 'manager', 'agent']
          },
          component: () => import('@/components/home/analytics/insights/main.vue')
        },
        {
          path: 'leaderboard',
          name: 'AnalyticsLeaderboard',
          meta: {
            roles: ['corp', 'manager', 'agent']
          },
          component: () => import('@/components/home/analytics/leaderboard/main.vue')
        },
        {
          path: 'live_event_insights',
          name: 'AnalyticsLiveEventInsights',
          meta: {
            roles: ['corp', 'manager', 'agent']
          },
          component: () => import('@/components/home/analytics/liveEventInsights/main.vue')
        }
      ]
    },
    {
      path: '/home/reporting',
      name: 'ExportPropertiesData',
      meta: {
        roles: ['corp', 'manager', 'agent']
      },
      component: () => import('@/components/home/reporting/main.vue')
    },
    {
      path: '/home/users-report',
      name: 'UsersReport',
      meta: {
        roles: ['corp', 'manager']
      },
      component: () => import('@/components/home/users/main.vue')
    },
    {
      path: '/properties',
      name: 'Properties',
      meta: {
        roles: ['corp', 'manager'],
        ownerHasAccess: true
      },
      component: () => import('@/components/media/properties/main.vue')
    },
    {
      path: '/properties/:propertyId',
      component: () => import('@/components/media/main.vue'),
      redirect: { name: 'PropertyMedia' },
      children: [
        {
          path: 'media',
          name: 'PropertyMedia',
          meta: {
            roles: ['corp', 'manager'],
            ownerHasAccess: true
          },
          component: () => import('@/components/media/MediaView.vue')
        },
        {
          path: 'list',
          name: 'PropertyMediaList',
          meta: {
            roles: ['corp', 'manager'],
            ownerHasAccess: true
          },
          component: () => import('@/components/media/list/main.vue')
        },
        { // Keep old `videos` routes redirects JIRA#DEV-7703
          path: 'videos',
          redirect: { name: 'PropertyMedia' }
        }
      ]
    },
    {
      path: '/property-units-overview',
      component: {
        template: '<router-view></router-view>'
      },
      children: [
        {
          name: 'PropertyUnitsOverview',
          path: '/',
          meta: {
            roles: ['corp', 'manager']
          },
          component: () => import('@/components/units-overview/PropertiesList/main.vue')
        },
        {
          path: ':propertyId',
          meta: {
            roles: ['corp', 'manager', 'agent']
          },
          component: () => import('@/components/units-overview/PropertyUnits/main.vue'),
          name: 'PropertyUnits'
        }
      ]
    },
    {
      path: '/live-video-tours',
      name: 'LiveVideoTours',
      meta: {
        roles: ['agent']
      },
      component: () => import('@/components/liveVideoTours/main.vue')
    },
    {
      path: '/live-video-tours/schedule',
      name: 'LiveVideoToursSchedule',
      meta: {
        roles: ['agent']
      },
      component: () => import('@/components/liveVideoTours/schedule/main.vue')
    },
    {
      path: '/live-video-tours/:tourId/edit',
      name: 'LiveVideoToursEdit',
      meta: {
        roles: ['agent']
      },
      component: () => import('@/components/liveVideoTours/schedule/main.vue')
    },
    {
      path: '/prospects',
      name: 'ProspectsIndex',
      meta: {
        roles: ['corp', 'manager', 'agent']
      },
      component: () => import('@/components/activity/main.vue')
    },
    {
      path: '/prospects/:prospectId',
      name: 'ProspectsShow',
      meta: {
        roles: ['corp', 'manager', 'agent']
      },
      component: () => import('@/components/activity/details/main.vue')
    },
    {
      path: '/settings',
      name: 'Settings',
      redirect: { name: 'SettingsAccount' },
      component: () => import('@/components/settings/main.vue'),
      children: [
        {
          path: 'account',
          name: 'SettingsAccount',
          meta: {
            roles: ['corp', 'manager', 'agent', 'contractor'],
            ownerHasAccess: true
          },
          component: () => import('@/components/settings/account/main.vue')
        },
        {
          path: 'customization',
          name: 'SettingsCustomization',
          meta: {
            roles: ['corp', 'manager', 'agent']
          },
          component: () => import('@/components/settings/customization/main.vue')
        },
        {
          path: 'notifications',
          name: 'SettingsNotifications',
          meta: {
            roles: ['corp', 'manager', 'agent']
          },
          component: () => import('@/components/settings/notifications/main.vue')
        },
        {
          path: 'live_tour_settings',
          name: 'SettingsLiveTour',
          meta: {
            roles: ['corp', 'manager', 'agent']
          },
          component: () => import('@/components/settings/live/main.vue')
        },
        {
          path: 'attachments',
          name: 'SettingsAttachments',
          meta: {
            roles: ['corp', 'manager', 'agent']
          },
          component: () => import('@/components/settings/attachments/main.vue')
        },
        {
          path: 'accessibility',
          name: 'SettingsAccessibility',
          meta: {
            roles: ['corp', 'manager', 'agent']
          },
          component: () => import('@/components/settings/accessibility/main.vue')
        },
        {
          path: 'security',
          name: 'SettingsSecurity',
          meta: {
            roles: ['corp', 'manager', 'agent', 'contractor'],
            ownerHasAccess: true
          },
          component: () => import('@/components/settings/security/main.vue')
        },
        {
          path: 'integrations',
          name: 'SettingsIntegrations',
          meta: {
            roles: ['corp', 'agent']
          },
          component: () => import('@/components/settings/integrations/main.vue')
        }
      ]
    },
    {
      path: '/realync-studios',
      name: 'RealyncStudios',
      component: () => import('@/components/realync-studios/main.vue'),
      children: [
        {
          path: 'dashboard',
          name: 'ContractorDashboard',
          meta: {
            roles: ['contractor']
          },
          component: () => import('@/components/realync-studios/ContractorDashboard/main.vue')
        },
        {
          path: 'requested-videos',
          name: 'RequestedRecordings',
          meta: {
            roles: ['corp', 'manager', 'agent'],
            checkToHaveRole: ['admin']
          },
          component: () => import('@/components/realync-studios/RequestedRecordings/main.vue')
        },
        {
          path: 'contractors',
          name: 'RealyncStudioContractors',
          meta: {
            roles: ['corp', 'manager', 'agent'],
            checkToHaveRole: ['admin']
          },
          component: () => import('@/components/realync-studios/Contractors/main.vue')
        },
        {
          path: 'contractors/:contractorId/properties/:propertyId/media',
          name: 'RealyncStudioContractorsMedia',
          meta: {
            roles: ['corp', 'manager', 'agent', 'contractor']
          },
          component: () => import('@/components/realync-studios/Contractors/media/main.vue')
        }
      ]
    }
  ]
};

export const publicRoutes = {
  path: '/',
  component: {
    template: '<router-view></router-view>'
  },
  children: [
    ...legacyStaticRoutes,
    {
      path: '/privacy_policy',
      name: 'PrivacyPolicy',
      component: PrivacyPolicy,
      meta: {
        publicPage: true,
        title: 'Realync | Privacy Policy'
      }
    },
    {
      path: '/past_video/past_session/:id',
      name: 'PastSession',
      component: () => import('@/components/live/sessions/past/main.vue'),
      meta: {
        publicPage: true,
        title: 'Realync | Past Session'
      }
    },
    {
      path: '/sign_in',
      name: 'SignIn',
      component: () => import('@/components/login/main.vue'),
      meta: {
        mobileNotSupported: true,
        publicPage: true,
        title: 'Realync | Sign In',
        redirectIfAuthorized: true
      }
    },
    {
      path: '/single-sign-on',
      name: 'SingleSignOn',
      component: () => import('@/components/login/main.vue'),
      meta: {
        mobileNotSupported: true,
        publicPage: true,
        title: 'Realync | Single Sign On',
        redirectIfAuthorized: true
      }
    },
    {
      path: '/multi-factor-authentication',
      name: 'MultiFactorAuthentication',
      component: () => import('@/components/login/main.vue'),
      meta: {
        title: 'Realync | Multi Factor Authentication',
        publicPage: true,
        redirectIfAuthorized: false
      }
    },
    {
      path: '/forgot_password',
      name: 'ForgotPassword',
      component: () => import('@/components/login/main.vue'),
      meta: {
        publicPage: true,
        title: 'Realync | Forgot Password',
        redirectIfAuthorized: true
      }
    },
    {
      path: '/reset_password/:resetHash',
      name: 'ResetPassword',
      component: () => import('@/components/login/PasswordActions.vue'),
      meta: {
        publicPage: true,
        title: 'Realync | Reset Password',
        redirectIfAuthorized: true
      }
    },
    {
      path: '/create_password/:resetHash',
      name: 'CreatePassword',
      component: () => import('@/components/login/PasswordActions.vue'),
      meta: {
        title: 'Realync | Change Password',
        publicPage: true,
        redirectIfAuthorized: true
      }
    },
    {
      path: '/update_password/:resetHash',
      name: 'UpdatePassword',
      component: () => import('@/components/login/PasswordActions.vue'),
      meta: {
        title: 'Realync | Update Password',
        publicPage: true,
        redirectIfAuthorized: true
      }
    },
    {
      path: '/browser_not_supported',
      name: 'BrowserNotSupported',
      component: () => import('@/components/pages/BrowserNotSupported.vue'),
      meta: {
        publicPage: true
      }
    },
    {
      path: '/loading',
      name: 'PageLoading',
      component: () => import('@/components/pages/PageLoading.vue'),
      meta: {
        publicPage: true
      }
    },
    {
      path: '/agent-busy',
      name: 'AgentBusy',
      component: () => import('@/components/pages/AgentBusy.vue'),
      meta: {
        skipBrowserNotSupported: true,
        title: 'Realync | Agent is busy',
        publicPage: true
      }
    },
    {
      path: '/link-invalid',
      name: 'LinkInvalid',
      component: () => import('@/components/pages/LinkInvalid.vue'),
      meta: {
        skipBrowserNotSupported: true,
        title: 'Realync | Link is invalid',
        publicPage: true
      }
    },
    {
      path: '/share',
      component: {
        template: '<router-view></router-view>'
      },
      children: [
        {
          path: ':type/:recordId',
          name: 'SharedVideo',
          component: () => import('@/components/sharePage/main.vue'),
          meta: {
            publicPage: true,
            skipBrowserNotSupported: true,
            title: 'Video | Real Estate Video Tours'
          }
        },
        {
          path: ':type/:recordId',
          name: 'SharedPlaylist',
          component: () => import('@/components/sharePage/main.vue'),
          meta: {
            publicPage: true,
            skipBrowserNotSupported: true,
            title: 'Playlist | Real Estate Video Tours'
          }
        }
      ]
    },
    {
      path: '/live-video-tour/:tourId',
      name: 'LiveVideoTourStream',
      component: () => import('@/components/liveVideoToursPage/main.vue'),
      meta: {
        publicPage: true,
        title: 'Realync | Live Video Tour'
      }
    },
    {
      path: '/live-video-tour/static/:agentId',
      name: 'LiveVideoToursStatic',
      component: () => import('@/components/liveVideoToursPage/main.vue'),
      meta: {
        publicPage: true,
        title: 'Realync | Live Video Tour'
      }
    },
    {
      path: '/live-video-tour/past/:tourId',
      name: 'PastLiveVideoTour',
      component: () => import('@/components/liveVideoToursPage/past/main.vue'),
      meta: {
        publicPage: true,
        title: 'Realync | Live Video Tour'
      }
    },
    {
      path: '/live-video-tour/:tourId/:propertyId',
      name: 'LiveVideoToursStaticInitiated',
      component: () => import('@/components/liveVideoToursPage/common/deepLinkStubPage.vue'),
      meta: {
        publicPage: true,
        title: 'Realync | Live Video Tour'
      }
    },
    {
      path: '/deeplink/live-video-tour',
      name: 'LiveVideoToursDeeplink',
      component: () => import('@/components/liveVideoToursPage/common/deepLinkStubPage.vue'),
      meta: {
        skipBrowserNotSupported: true,
        publicPage: true,
        title: 'Realync | Live Video Tour'
      }
    },
    {
      path: '/past_video/past',
      name: 'PastVideoTour',
      component: () => import('@/components/live/tours/past/main.vue'),
      meta: {
        publicPage: true,
        title: 'Realync | Past Tour'
      }
    }
  ]
};

export default {
  videoEditRoutes,
  videoLinkRoute,
  reviewRedirectRoute,
  inAppRoutes,
  publicRoutes
};
